import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Stack,
  Divider,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  alpha,
  InputAdornment,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  Layers,
  Agriculture,
  Terrain,
  Assignment,
  Straighten,
  Palette,
  EditNote,
  Science,
  WaterDrop,
  Opacity,
  WbSunny,
  Grain,
  Texture,
  Numbers,
  Category,
  DateRange,
  Person,
  ContentCopy,
  Settings,
  FilterHdr,
  Landscape,
  TrendingUp,
  Warning,
  Delete,
  Add,
  MyLocation,
  Public,
  LocationOn,
} from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import api from "../../api";
import DataTableFrame from "../../components/common/DataTableFrame";

import {
  FIZYOGRAFYA_OPTIONS,
  ANA_MATERYAL_OPTIONS,
  BTG_OPTIONS,
  EGIM_YUZDE_OPTIONS,
  TASLILIK_OPTIONS,
  CAKILLILIK_OPTIONS,
  KAYALILIK_OPTIONS,
  URUN_OPTIONS,
  SU_EROZYON_DERECESI_OPTIONS,
  TABII_DRENAJ_OPTIONS,
  ARAZI_KULLANIM_KATEGORI_OPTIONS,
} from "../../constants/fieldConstants";

import {
  TEKSTUR_OPTIONS,
  KIREC_OPTIONS,
  MUNSELL_HUE_OPTIONS,
  MUNSELL_VALUE_CHROMA_OPTIONS,
  HORIZON_TEMPLATE,
} from "../../constants/horizonConstants";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const EditSubHeader = ({ title, icon, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mb: 2, mt: 4 }}
  >
    <Box
      sx={{
        display: "flex",
        p: 0.8,
        borderRadius: "8px",
        bgcolor: alpha(color, 0.1),
        color: color,
      }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Box>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "text.primary",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ flexGrow: 1, borderColor: alpha(color, 0.2) }} />
  </Stack>
);

const RenderSelect = ({ label, value, options, onChange, disabled }) => (
  <Autocomplete
    freeSolo
    disabled={disabled}
    options={options || []}
    filterOptions={(options, state) =>
      options.filter((item) =>
        item
          .toString()
          .toUpperCase()
          .startsWith(state.inputValue.toUpperCase()),
      )
    }
    value={value || ""}
    onChange={(e, val) => onChange(val)}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        size="small"
        variant="outlined"
        disabled={disabled}
      />
    )}
  />
);

export default function SondaEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    sonda_no: "",
    details: { sonda_tipi: "Örnek", horizons: [] },
  });

  // NOT: Kontrol sonda + benzer sonda secildiginde eskiden diger alanlar
  // kitleniyordu. Musteri talebiyle kaldirildi: hicbir alan kilitlenmez,
  // girilen/kopyalanan her deger degistirilebilir.

  useEffect(() => {
    const fetchSonda = async () => {
      try {
        setLoading(true);
        // Doğrudan id'ye göre sorgu atıyoruz
        const res = await api.get(`/admin/sonda-detail/${id}`);
        const sonda = res.data.data;

        if (sonda) {
          const parsedDetails =
            typeof sonda.full_data === "string"
              ? JSON.parse(sonda.full_data)
              : sonda.full_data;

          setFormData({
            sonda_no: sonda.sonda_no || "",
            details: parsedDetails || {},
          });
        }
        setLoading(false);
      } catch (err) {
        console.error("Sonda yükleme hatası:", err);
        setLoading(false);
      }
    };
    fetchSonda();
  }, [id]);

  const handleDetailChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      details: { ...prev.details, [field]: value },
    }));
  };

  const handleSondaTipiToggle = (tip) => {
    setFormData((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        sonda_tipi: tip,
        ...(tip === "Kontrol" ? { cuval_no: "-", alma_sirasi: "-" } : {}),
      },
    }));
  };

  const handleHorizonChange = (index, field, value) => {
    const newHorizons = [...(formData.details.horizons || [])];
    if (field === "derinlik_max") {
      const nextIndex = index + 1;
      if (nextIndex < newHorizons.length) {
        newHorizons[nextIndex] = {
          ...newHorizons[nextIndex],
          derinlik_min: value,
        };
      }
    }
    newHorizons[index] = { ...newHorizons[index], [field]: value };
    handleDetailChange("horizons", newHorizons);
  };

  const addHorizon = () => {
    const newHorizons = [...(formData.details.horizons || [])];
    const lastHorizon = newHorizons[newHorizons.length - 1];
    const startDepth = lastHorizon
      ? parseFloat(lastHorizon.derinlik_max) || 0
      : 0;
    newHorizons.push({
      ...HORIZON_TEMPLATE,
      no: newHorizons.length + 1,
      derinlik_min: startDepth,
    });
    handleDetailChange("horizons", newHorizons);
  };

  const removeHorizon = (index) => {
    if (
      window.confirm(`${index + 1}. Katmanı silmek istediğinize emin misiniz?`)
    ) {
      const newHorizons = formData.details.horizons.filter(
        (_, i) => i !== index,
      );
      handleDetailChange("horizons", newHorizons);
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/admin/update-sonda/${id}`, {
        sonda_no: formData.sonda_no,
        full_data: formData.details,
      });
      alert("✅ Sonda başarıyla güncellendi!");
      navigate("/admin/sondas");
    } catch (err) {
      alert("❌ Güncelleme sırasında hata oluştu!");
    }
  };

  if (loading)
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>Veriler hazırlanıyor...</Box>
    );

  return (
    <Box sx={{ p: 3, bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{ borderRadius: "10px" }}
        >
          İptal / Geri
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 900, color: "#2c3e50" }}>
          SONDA DÜZENLEME PANELİ
        </Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{ borderRadius: "10px", px: 4, py: 1.2, fontWeight: "bold" }}
        >
          Verileri Güncelle
        </Button>
      </Stack>

      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <EditSubHeader
            title="1. Sonda Kimlik ve Proje Bilgileri"
            icon={<Assignment />}
            color="#1976d2"
          />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              p: 1,
              bgcolor: "#f8f9fa",
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.details.sonda_tipi === "Örnek"}
                  onChange={() => handleSondaTipiToggle("Örnek")}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" fontWeight={700}>
                  Örnek Sonda
                </Typography>
              }
            />
            {/* YENİ EKLENDİ: İSTİKŞAFİ SONDA */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.details.sonda_tipi === "İstikşafi"}
                  onChange={() => handleSondaTipiToggle("İstikşafi")}
                  color="info"
                />
              }
              label={
                <Typography variant="body2" fontWeight={700} color="info.main">
                  İstikşafi Sonda
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.details.sonda_tipi === "Kontrol"}
                  onChange={() => handleSondaTipiToggle("Kontrol")}
                  color="secondary"
                />
              }
              label={
                <Typography variant="body2" fontWeight={700} color="secondary">
                  Kontrol Sonda
                </Typography>
              }
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          <TextField
            label="Sonda No"
            size="small"
            value={formData.sonda_no}
            onChange={(e) =>
              setFormData({ ...formData, sonda_no: e.target.value })
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Numbers fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Grup No"
            size="small"
            value={formData.details.grup_no || ""}
            onChange={(e) => handleDetailChange("grup_no", e.target.value)}
          />
          {formData.details.sonda_tipi === "Kontrol" && (
            <TextField
              label="Benzer Sonda No"
              size="small"
              value={formData.details.benzer_sonda_no || ""}
              onChange={(e) =>
                handleDetailChange("benzer_sonda_no", e.target.value)
              }
            />
          )}
          <TextField
            label="Benzer Profil"
            size="small"
            value={formData.details.benzer_profil || ""}
            onChange={(e) =>
              handleDetailChange("benzer_profil", e.target.value)
            }
          />
          <TextField
            label="Çalışma Adı"
            size="small"
            value={formData.details.calismanin_adi || ""}
            onChange={(e) =>
              handleDetailChange("calismanin_adi", e.target.value)
            }
          />
          <TextField
            label="Tarih"
            type="date"
            size="small"
            value={formData.details.tarih || ""}
            onChange={(e) => handleDetailChange("tarih", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Örneği Alan Personel"
            size="small"
            value={formData.details.ornegi_alan || ""}
            onChange={(e) => handleDetailChange("ornegi_alan", e.target.value)}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
            mt: 2,
          }}
        >
          <TextField
            label="Koordinat X"
            size="small"
            value={formData.details.koordinat_x || ""}
            onChange={(e) => handleDetailChange("koordinat_x", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MyLocation fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Koordinat Y"
            size="small"
            value={formData.details.koordinat_y || ""}
            onChange={(e) => handleDetailChange("koordinat_y", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MyLocation fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Yükseklik (m)"
            type="number"
            size="small"
            value={formData.details.yukseklik || ""}
            onChange={(e) => handleDetailChange("yukseklik", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Terrain fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
            mt: 2,
          }}
        >
          <TextField
            label="İl"
            size="small"
            value={formData.details.il || formData.details.sehir || ""}
            onChange={(e) => handleDetailChange("il", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Public fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="İlçe"
            size="small"
            value={formData.details.ilce || ""}
            onChange={(e) => handleDetailChange("ilce", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Mahalle / Köy"
            size="small"
            value={formData.details.mahalle || ""}
            onChange={(e) => handleDetailChange("mahalle", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px", bgcolor: "#fcfcfd" }}>
        <EditSubHeader
          title="2. Lojistik ve Örnekleme"
          icon={<Settings />}
          color="#455a64"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label="Çuval No"
            size="small"
            value={formData.details.cuval_no || ""}
            onChange={(e) => handleDetailChange("cuval_no", e.target.value)}
          />
          <TextField
            label="Alma Sırası"
            size="small"
            type="number"
            value={formData.details.alma_sirasi || ""}
            onChange={(e) => handleDetailChange("alma_sirasi", e.target.value)}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px" }}>
        <EditSubHeader
          title="3. Arazi ve Toprak Karakteristiği"
          icon={<Terrain />}
          color="#2e7d32"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          <RenderSelect
            label="Arazi Kullanım Sınıfı"
            value={formData.details.arazi_kullanim_sinif}
            options={ARAZI_KULLANIM_KATEGORI_OPTIONS}
            onChange={(val) => handleDetailChange("arazi_kullanim_sinif", val)}
          />
          <RenderSelect
            label="Ana Materyal"
            value={formData.details.ana_materyal}
            options={ANA_MATERYAL_OPTIONS}
            onChange={(val) => handleDetailChange("ana_materyal", val)}
          />
          <RenderSelect
            label="Büyük Toprak Grubu"
            value={formData.details.btg}
            options={BTG_OPTIONS}
            onChange={(val) => handleDetailChange("btg", val)}
          />
          <RenderSelect
            label="Fizyografya"
            value={formData.details.fizyografya}
            options={FIZYOGRAFYA_OPTIONS}
            onChange={(val) => handleDetailChange("fizyografya", val)}
          />
          <RenderSelect
            label="Eğim (%)"
            value={formData.details.egim}
            options={EGIM_YUZDE_OPTIONS}
            onChange={(val) => handleDetailChange("egim", val)}
          />
          <TextField
            label="Derinlik (cm)"
            type="number"
            size="small"
            value={formData.details.derinlik || ""}
            onChange={(e) => handleDetailChange("derinlik", e.target.value)}
          />
          <RenderSelect
            label="Drenaj"
            value={formData.details.drenaj}
            options={TABII_DRENAJ_OPTIONS}
            onChange={(val) => handleDetailChange("drenaj", val)}
          />
          <RenderSelect
            label="Su Erozyonu"
            value={formData.details.su_erozyonu}
            options={SU_EROZYON_DERECESI_OPTIONS}
            onChange={(val) => handleDetailChange("su_erozyonu", val)}
          />
          <RenderSelect
            label="Ürün"
            value={formData.details.urun}
            options={URUN_OPTIONS}
            onChange={(val) => handleDetailChange("urun", val)}
          />
          <RenderSelect
            label="Taşlılık"
            value={formData.details.taslilik}
            options={TASLILIK_OPTIONS}
            onChange={(val) => handleDetailChange("taslilik", val)}
          />
          <RenderSelect
            label="Çakıllık"
            value={formData.details.cakillilik}
            options={CAKILLILIK_OPTIONS}
            onChange={(val) => handleDetailChange("cakillilik", val)}
          />
          <RenderSelect
            label="Kayalılık"
            value={formData.details.kayalilik}
            options={KAYALILIK_OPTIONS}
            onChange={(val) => handleDetailChange("kayalilik", val)}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: "16px", overflow: "hidden" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <EditSubHeader
            title="4. Katman (Horizon) Analizleri"
            icon={<Layers />}
            color="#7b1fa2"
          />
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Add />}
            onClick={addHorizon}
          >
            Yeni Katman Ekle
          </Button>
        </Stack>

        <DataTableFrame
          plain
          size="small"
          minWidth={1200}
          maxHeight={null}
          stickyHeader={false}
        >
            <TableHead sx={{ bgcolor: "#f8f9fa" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, width: 80 }}>
                  Sembol
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 160 }}>
                  Derinlik (cm)
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 200 }}>
                  Tekstür & Kireç
                </TableCell>
                <TableCell sx={{ fontWeight: 800, width: 200 }}>
                  Munsell Renk
                </TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Not</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800, width: 50 }}>
                  Sil
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.details.horizons?.map((h, idx) => {
                return (
                  <TableRow key={idx} hover>
                    <TableCell valign="top">
                      <TextField
                        fullWidth
                        variant="standard"
                        value={h.sembol || ""}
                        onChange={(e) =>
                          handleHorizonChange(idx, "sembol", e.target.value)
                        }
                        placeholder="Sembol"
                      />
                    </TableCell>
                    <TableCell valign="top">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <TextField
                          size="small"
                          type="number"
                          sx={{ width: 65 }}
                          value={
                            h.derinlik_min === 0 ? 0 : h.derinlik_min || ""
                          }
                          onChange={(e) =>
                            handleHorizonChange(
                              idx,
                              "derinlik_min",
                              e.target.value,
                            )
                          }
                        />
                        <Typography>-</Typography>
                        <TextField
                          size="small"
                          type="number"
                          sx={{ width: 65 }}
                          value={h.derinlik_max || ""}
                          disabled={h.derinlik_arti}
                          onChange={(e) =>
                            handleHorizonChange(
                              idx,
                              "derinlik_max",
                              e.target.value,
                            )
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={h.derinlik_arti || false}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                handleHorizonChange(
                                  idx,
                                  "derinlik_arti",
                                  isChecked,
                                );
                                if (isChecked)
                                  handleHorizonChange(idx, "derinlik_max", 200);
                              }}
                            />
                          }
                          label="+"
                          sx={{ m: 0 }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell valign="top">
                      <Stack spacing={1}>
                        <RenderSelect
                          label="Tekstür"
                          value={h.tekstur}
                          options={TEKSTUR_OPTIONS}
                          onChange={(val) =>
                            handleHorizonChange(idx, "tekstur", val)
                          }
                        />
                        <RenderSelect
                          label="Kireç"
                          value={h.kirec}
                          options={KIREC_OPTIONS}
                          onChange={(val) =>
                            handleHorizonChange(idx, "kirec", val)
                          }
                        />
                      </Stack>
                    </TableCell>
                    <TableCell valign="top">
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1}>
                          <RenderSelect
                            label="K.Hue"
                            value={h.renk_sinif_kuru}
                            options={MUNSELL_HUE_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_sinif_kuru", val)
                            }
                          />
                          <RenderSelect
                            label="Kod"
                            value={h.renk_kod_kuru}
                            options={MUNSELL_VALUE_CHROMA_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_kod_kuru", val)
                            }
                          />
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <RenderSelect
                            label="Y.Hue"
                            value={h.renk_sinif_yas}
                            options={MUNSELL_HUE_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_sinif_yas", val)
                            }
                          />
                          <RenderSelect
                            label="Kod"
                            value={h.renk_kod_yas}
                            options={MUNSELL_VALUE_CHROMA_OPTIONS}
                            onChange={(val) =>
                              handleHorizonChange(idx, "renk_kod_yas", val)
                            }
                          />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell valign="top">
                      <TextField
                        fullWidth
                        label="Gözlem"
                        size="small"
                        multiline
                        rows={2}
                        value={h.gozlem || ""}
                        onChange={(e) =>
                          handleHorizonChange(idx, "gozlem", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell valign="middle" align="center">
                      <IconButton
                        color="error"
                        disabled={formData.details.horizons?.length === 1}
                        onClick={() => removeHorizon(idx)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
        </DataTableFrame>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: "16px" }}>
        <EditSubHeader
          title="5. Genel Gözlemler & Notlar"
          icon={<EditNote />}
          color="#607d8b"
        />
        <TextField
          fullWidth
          multiline
          rows={4}
          value={formData.details.gozlemler || ""}
          onChange={(e) => handleDetailChange("gozlemler", e.target.value)}
        />
      </Paper>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{
            borderRadius: "12px",
            px: 10,
            py: 2,
            fontSize: "1.1rem",
            fontWeight: "bold",
            boxShadow: "0 8px 30px rgba(46, 125, 50, 0.3)",
          }}
        >
          TÜM VERİLERİ KAYDET
        </Button>
      </Box>
    </Box>
  );
}
